import { LoaderCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { authClient } from "../../lib/auth-client";
import { useAdminAuth } from "../../lib/use-admin-auth";

const logoLight = "/branding/logo-light.svg";

export function LoginPage() {
  const { sessionData, isPending, bootstrapLoading } = useAdminAuth();
  const [signInMode, setSignInMode] = useState<"email" | "passkey">("email");
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const otpInputRef = useRef<HTMLInputElement | null>(null);

  const resetEmailFlow = () => {
    setEmailOtp("");
    setEmailOtpSent(false);
    setError("");
  };

  const handlePasskeySignIn = async () => {
    setIsPasskeyLoading(true);
    setError("");
    try {
      const { error } = await authClient.signIn.passkey();
      if (error) setError(error.message || "Passkey-innlogging feilet");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Noe gikk galt");
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  const sendEmailOtp = async () => {
    setIsEmailLoading(true);
    setError("");
    if (!email) {
      setError("Skriv inn e-postadresse");
      setIsEmailLoading(false);
      return;
    }
    try {
      const { error: otpError } = await authClient.emailOtp.sendVerificationOtp({
        email,
        type: "sign-in",
      });
      if (otpError) setError(otpError.message || "Kunne ikke sende kode");
      else setEmailOtpSent(true);
    } catch {
      setError("Noe gikk galt. Prøv igjen.");
    } finally {
      setIsEmailLoading(false);
    }
  };

  const verifyEmailOtp = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsEmailLoading(true);
    setError("");
    if (emailOtp.length !== 6) {
      setError("Koden må være 6 siffer");
      setIsEmailLoading(false);
      return;
    }
    try {
      const res = await authClient.signIn.emailOtp({ email, otp: emailOtp });
      if (res.error) setError(res.error.message || "Ugyldig kode");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Noe gikk galt");
    } finally {
      setIsEmailLoading(false);
    }
  };

  useEffect(() => {
    if (!emailOtpSent) return;
    const frame = window.requestAnimationFrame(() => {
      otpInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [emailOtpSent]);

  if (isPending || (sessionData?.user && bootstrapLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoaderCircle className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (sessionData?.user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-background lg:grid lg:grid-cols-2">
      <div className="pointer-events-none absolute left-0 top-0 -z-10 h-full w-full overflow-hidden">
        <div className="absolute left-[-10%] top-[-10%] h-[40%] w-[40%] rounded-full bg-accent/12 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[40%] h-[30%] w-[30%] rounded-full bg-primary/8 blur-[100px]" />
      </div>

      <div className="relative z-10 flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 flex flex-col gap-1.5">
            <img src={logoLight} alt="Forhandlerbørsen" className="h-14 w-auto mr-auto" />
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-accent pl-1">
              Admin Console
            </div>
          </div>

          <div className="mb-8">
            <h1 className="mb-2 text-4xl font-black tracking-tight text-primary">Administrator-innlogging</h1>
            <p className="text-base text-muted-foreground">Kun for autoriserte administratorer.</p>
          </div>

          <div className="glass relative overflow-hidden rounded-[2rem] border border-white/50 p-6 sm:p-8">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
            <div className="relative z-10 flex flex-col gap-6">
              {error ? <ErrorAlert message={error} /> : null}

              <Button
                className="h-auto w-full py-5 text-base font-bold transition-all hover:-translate-y-1"
                onClick={() => void handlePasskeySignIn()}
                disabled={isPasskeyLoading}
              >
                {isPasskeyLoading ? (
                  <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                ) : (
                  <PasskeyIcon />
                )}
                Logg inn med passkey
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-secondary px-3 font-medium text-muted-foreground">eller med e-post</span>
                </div>
              </div>

              <form
                onSubmit={
                  emailOtpSent
                    ? verifyEmailOtp
                    : (e) => {
                      e.preventDefault();
                      void sendEmailOtp();
                    }
                }
                className="flex flex-col gap-4"
              >
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-foreground/80">E-postadresse</label>
                  <Input
                    type="email"
                    placeholder="admin@forhandlerborsen.no"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    disabled={emailOtpSent}
                    required
                    className="h-11 rounded-xl transition-colors focus:bg-white"
                  />
                </div>
                {emailOtpSent ? (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground/80">
                      Engangskode (sjekk e-posten din)
                    </label>
                    <Input
                      ref={otpInputRef}
                      type="text"
                      value={emailOtp}
                      onChange={(e) => setEmailOtp(e.target.value)}
                      maxLength={6}
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      placeholder="123456"
                      className="h-11 rounded-xl tracking-[0.5em] text-center text-lg font-semibold focus:bg-white"
                    />
                    <button
                      type="button"
                      onClick={resetEmailFlow}
                      className="self-start text-xs text-muted-foreground hover:text-primary transition"
                    >
                      Bruk en annen e-post
                    </button>
                  </div>
                ) : null}
                <Button type="submit" className="mt-1 h-11 w-full rounded-xl" disabled={isEmailLoading}>
                  {isEmailLoading ? (
                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  {emailOtpSent ? "Logg inn" : "Send engangskode"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Brand side */}
      <div className="relative hidden items-center justify-center overflow-hidden border-l border-white/10 bg-primary p-12 lg:flex">
        <div className="absolute inset-0 bg-primary" />
        <div className="absolute right-[-10%] top-[-10%] h-[70%] w-[70%] animate-pulse rounded-full bg-accent/35 opacity-70 blur-[100px] mix-blend-screen" style={{ animationDuration: "8s" }} />
        <div className="absolute bottom-[-10%] left-[-10%] h-[60%] w-[60%] animate-pulse rounded-full bg-white/10 opacity-70 blur-[120px] mix-blend-screen" style={{ animationDuration: "12s", animationDelay: "2s" }} />
        <div className="absolute left-[20%] top-[20%] h-[40%] w-[40%] animate-pulse rounded-full bg-accent/20 opacity-60 blur-[80px] mix-blend-screen" style={{ animationDuration: "10s", animationDelay: "4s" }} />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff12_1px,transparent_1px),linear-gradient(to_bottom,#ffffff12_1px,transparent_1px)] bg-[size:32px_32px] opacity-40 [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_30%,transparent_100%)]" />

        <div className="relative z-10 w-full max-w-lg space-y-6 text-foreground">
          <div className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm font-semibold text-white/80 backdrop-blur-xl">
            <span className="mr-2 flex h-2 w-2 rounded-full bg-accent" />
            Admin Console
          </div>
          <h2 className="text-balance text-5xl font-black tracking-tight text-white">
            Fullt kontroll over plattformen
          </h2>
          <p className="text-balance text-xl font-medium text-white/60">
            Administrer organisasjoner, brukere, auksjoner og oppgaver på ett sted.
          </p>
        </div>
      </div>
    </div>
  );
}

function ErrorAlert({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-destructive/15 bg-destructive/8 px-4 py-3 text-sm leading-6 text-destructive">
      {message}
    </div>
  );
}

function PasskeyIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 h-5 w-5">
      <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
      <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
    </svg>
  );
}
